/**
 * Startup checks (P1 — startup network tax).
 *
 * Two best-effort startup steps used to run serially at the top of `index.ts`,
 * each with a ~2s network cap, BEFORE any command dispatch:
 *
 *   1. `enforceVersionPolicy` — the server-driven min-version floor (backlog 200).
 *   2. `checkForUpdates` — the friendly npm-registry upgrade nudge.
 *
 * Serially that is ~4s of latency on every cold invocation. In agent, CI, and
 * other non-interactive contexts the interactive upgrade nudge can't even be
 * shown, so that tax buys nothing — it just slows every `grid` call an agent
 * makes. This module (a) skips both checks for non-interactive / agent / CI
 * callers (or when `CLOUDGRID_NO_STARTUP_CHECKS=1`), and (b) runs them in
 * parallel for interactive humans, who keep the upgrade nudge.
 *
 * Extracted from `index.ts` so the skip/parallel logic is unit-testable without
 * importing the module for its parse side effect.
 */
import { checkForUpdates as defaultCheckForUpdates } from './update-check.js';
import { enforceVersionPolicy as defaultEnforceVersionPolicy } from './version-policy.js';
import { detectCallerKind } from './services/telemetry-context.js';

const SKIP_ENV = 'CLOUDGRID_NO_STARTUP_CHECKS';

/**
 * Decide whether the startup network checks should run this invocation.
 *
 * Runs ONLY for an interactive human CLI session (`detectCallerKind` → `cli`,
 * which requires a TTY and no agent/CI markers). Agent, CI, MCP-stdio, and
 * bare non-interactive invocations skip — as does any caller that sets
 * `CLOUDGRID_NO_STARTUP_CHECKS=1`. Reuses the same caller-kind signals the
 * telemetry pipeline already relies on so there's one definition of "agent".
 */
export function shouldRunStartupChecks(
  env: NodeJS.ProcessEnv = process.env,
  isTty: boolean = Boolean(process.stdout.isTTY),
): boolean {
  if (env[SKIP_ENV] === '1') return false;
  return detectCallerKind(env, isTty) === 'cli';
}

export interface StartupCheckDeps {
  /** Override the env used for the skip decision (test seam). */
  env?: NodeJS.ProcessEnv;
  /** Override the TTY signal used for the skip decision (test seam). */
  isTty?: boolean;
  /** Override the version-policy enforcer (test seam). */
  enforceVersionPolicy?: (version: string) => Promise<void>;
  /** Override the update-nudge check (test seam). */
  checkForUpdates?: (version: string) => Promise<void>;
}

/**
 * True unless the caller explicitly opted out with `CLOUDGRID_NO_STARTUP_CHECKS=1`.
 *
 * The server-driven version policy (`enforceVersionPolicy`, backlog 200) is the
 * `block` kill-switch that stops an incompatible CLI. Unlike the update nudge, a
 * `block` IS actionable for EVERY caller kind — an out-of-date agent/CI/MCP-stdio
 * CLI should be stoppable too (that population is where a silent version
 * incompatibility does the most damage). So it runs for all callers, gated only
 * by the explicit opt-out — not by the interactive-only `shouldRunStartupChecks`.
 * Its own 2s cap + 5-min cache keep the cost bounded/amortized.
 */
export function shouldEnforceVersionPolicy(
  env: NodeJS.ProcessEnv = process.env,
): boolean {
  return env[SKIP_ENV] !== '1';
}

/**
 * Run the startup checks for `version`. The version-policy gate runs for all
 * callers (unless {@link shouldEnforceVersionPolicy} opts out); the interactive
 * npm upgrade nudge runs only for an interactive human session
 * ({@link shouldRunStartupChecks}). Both fire in parallel and each swallows its
 * own errors so a flaky network never blocks (or crashes) command dispatch. A
 * `block`-level version policy still calls `process.exit` from inside
 * `enforceVersionPolicy`, terminating the process regardless of the update poll.
 */
export async function runStartupChecks(
  version: string,
  deps: StartupCheckDeps = {},
): Promise<void> {
  const env = deps.env ?? process.env;
  const enforce = deps.enforceVersionPolicy ?? defaultEnforceVersionPolicy;
  const check = deps.checkForUpdates ?? defaultCheckForUpdates;
  const runEnforce = shouldEnforceVersionPolicy(env);
  const runNudge = shouldRunStartupChecks(deps.env, deps.isTty);
  await Promise.all([
    runEnforce ? enforce(version).catch(() => {}) : Promise.resolve(),
    runNudge ? check(version).catch(() => {}) : Promise.resolve(),
  ]);
}
